
import { PDFDocument } from 'pdf-lib';
import { PDFName,PDFCheckBox } from 'pdf-lib';
import { rgb, StandardFonts } from 'pdf-lib';

import medianData from '../../json/median_income.json';
import { generateLongFormMeansTestPdf } from './LongFormMeansTestGenerator';
import {
  filingDebtors as getFilingDebtors,
  sortDebtorsPrimaryFirst,
} from '../../utils/debtors';
import { formatEmploymentLength } from '../../utils/employmentDuration';
import {
  amendmentServiceDescription,
  amendmentServiceRecipients,
  appendAmendmentServiceList,
} from '../../utils/creditorMailingMatrix';

// Helper to log form field names
const API_URL2 = process.env.REACT_APP_BACKEND_URL;
const token = localStorage.getItem("accessToken");
async function generateCreditorsListPdf(creditors) {
    // Create the document
    const listDoc = await PDFDocument.create();
    creditors = Array.isArray(creditors) ? creditors : [];
    
    // Deduplicate by name & address
    const seen = new Set();
    creditors = creditors.filter(c => {
      const key = [c.name, c.street1, c.street2 || '', c.city, c.state, c.zip_code]
        .join('|');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  
    const helvetica = await listDoc.embedFont(StandardFonts.Helvetica);
    const pageSize = [612, 792]; // Letter size
    let page = listDoc.addPage(pageSize);
    const { width, height } = page.getSize();
    const margin = 50;
    let y = height - margin;
    const lineHeight = 12;
    const indent = margin + 10;

    if (creditors.length === 0) {
      const message = 'No additional creditors were served.';
      const fontSize = 11;
      const textWidth = helvetica.widthOfTextAtSize(message, fontSize);
      page.drawText(message, {
        x: (width - textWidth) / 2,
        y: height - 100,
        size: fontSize,
        font: helvetica,
      });
      return listDoc;
    }
  
    for (const c of creditors) {
      // Name
      page.drawText(c.name, { x: margin, y, size: 10, font: helvetica });
      y -= lineHeight;
      // Address line 1
      page.drawText(
        `${c.street1}${c.street2?.trim() ? ', ' + c.street2 : ''}`,
        { x: indent, y, size: 10, font: helvetica }
      );
      y -= lineHeight;
      // City, State Zip
      page.drawText(
        `${c.city}, ${c.state} ${c.zip_code}`,
        { x: indent, y, size: 10, font: helvetica }
      );
      y -= lineHeight;
      // Method
      page.drawText(`Served via: ${c.method}`, { x: indent, y, size: 10, font: helvetica });
      y -= lineHeight * 1.5;
  
      if (y < margin) {
        page = listDoc.addPage(pageSize);
        y = height - margin;
      }
    }
  
    return listDoc;
  }
function moveUncheckedCheckboxesToCorner(form, pageSize, context) {
  const { width, height } = pageSize;
  form.getFields().forEach(field => {
    // Only target PDFCheckBox fields
    if (field instanceof PDFCheckBox) {
      try {
        if (!field.isChecked()) {
          const widgets = field.acroField.getWidgets();
          widgets.forEach(widget => {
            const dict = widget.acroAnnotation?.dict || widget.dict;
            dict.set(
              PDFName.of('Rect'),
              context.obj([
                width + 5,
                height + 5,
                width + 15,
                height + 15
              ])
            );
          });
        }
      } catch {
        // ignore any errors moving individual checkboxes
      }
    }
  });
}
  async function generateEnvelopesPdf(creditors) {
    if (!creditors || creditors.length === 0) return;
    const envDoc = await PDFDocument.create();
    const seen = new Set();
    creditors = creditors.filter(c => {
      const key = [c.name, c.street1, c.street2 || '', c.city, c.state, c.zip_code]
        .join('|');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    // 1. Load & embed your logo (adjust the path as needed)
    const logoUrl = '/images/logo.png';
    const logoBytes = await fetch(logoUrl).then(r => r.arrayBuffer());
    const logoImage = await envDoc.embedPng(logoBytes);
    const logoDims = logoImage.scale(0.1);
  
    // 2. Prepare font and layout constants
    const font = await envDoc.embedFont(StandardFonts.Helvetica);
    const fontSize = 10;
    const lineHeight = fontSize + 4;
    const envelopeSize = [9.5 * 72, 4.125 * 72]; // width × height in points
    const margin = 25;
  
    // Your return address lines
    const returnAddress = [
      'Cisowski Law',
      '3119 Bristol Hwy Ste 217',
      'Johnson City, TN 37601',
    ];
  
    // 3. One page per creditor
    for (const c of creditors) {
      const page = envDoc.addPage(envelopeSize);
      const { width, height } = page.getSize();
  
      // 3a. Draw logo in top-left
      page.drawImage(logoImage, {
        x: margin,
        y: height - margin - logoDims.height,
        width: logoDims.width,
        height: logoDims.height,
      });
  
      // 3b. Draw return address to the right of the logo
      let yPos = height - margin - 9;
      for (const line of returnAddress) {
        page.drawText(line, {
          x: margin + logoDims.width + 10,
          y: yPos,
          size: fontSize,
          font,
        });
        yPos -= fontSize + 2;
      }
  
      // draw recipient address centered vertically and horizontally
      const addrLines = [
        c.name,
        `${c.street1}${c.street2?.trim() ? `, ${c.street2}` : ''}`,
        `${c.city}, ${c.state} ${c.zip_code}`,
      ];
      // measure max line width
      const lineWidths = addrLines.map(line => font.widthOfTextAtSize(line, fontSize));
      const maxWidth = Math.max(...lineWidths);
      const startX = (width - maxWidth) / 2;
      let startY = height / 2 + (addrLines.length / 2) * lineHeight;
      for (const line of addrLines) {
        page.drawText(line, {
          x: startX,
          y: startY,
          size: fontSize,
          font,
        });
        startY -= lineHeight;
      }
    }
  
    // 4. Save & trigger download
    const pdfBytes = await envDoc.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'envelopes.pdf';
    link.click();
  }
function logFieldNames(form, label) {
  console.log(`--- ${label} Fields ---`);
  form.getFields().forEach(field => {
    console.log(field.getName());
  });
}

const fmtDate = (s) => {
      if (!s) return '';
      const [year, month, day] = s.split('-');
      return `${month}/${day}/${year}`;
    };
    // helper to format dates as a long string, e.g. "Thursday, May 29, 2025"
    const fmtLongDate = (s) => {
      if (!s) return '';
      const [year, month, day] = s.split('-').map(Number);
      // Construct date in local timezone to avoid UTC offset issues
      const d = new Date(year, month - 1, day);
      return d.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    };

async function fetchPdf(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error('Failed to fetch PDF: ' + url);
  return new Uint8Array(await res.arrayBuffer());
}

export async function generateScheduleIJ(caseData, expenses, expenseSheet, dependents, installmentPayments, selectedSchedules, data, meansTestIncomes = [], maritalAdjustments = [], longFormData = {}, options = {}) {
  caseData = { ...caseData, debtors: sortDebtorsPrimaryFirst(caseData?.debtors) };
  // Variables needed in both Schedule I and Schedule J
  let expenseContribAmounts = [];
  let combined = 0;
  const hasSupplement = caseData.debtors.some(d =>
    (d.business_incomes?.length || 0) > 0 || (d.employments?.length || 0) > 1
  );
  // Load Schedule I template
  const mergedPdf = await PDFDocument.create();
  const mergedPdf122 = await PDFDocument.create();
  let form122Blob;
  let form122ShortBlob = null;
  let form122LongBlob = null;



  // Helper to compute gross wages net of deductions for one employment
  const calcGrossWages = emp => {
    const rate = Number(emp.pay_rate) || 0;
    const units = Number(emp.pay_units) || 0;
    const frequencyFactor = (() => {
      switch (emp.pay_schedule) {
        case 'hourly': return (units * 52) / 12;
        case 'weekly': return 52 / 12;
        case 'biweekly': return 26 / 12;
        case 'semimonthly': return 2;
        case 'monthly': return 1;
        case 'yearly': return 1 / 12;
        default: return 0;
      }
    })();
    const gross = rate * frequencyFactor;
    const deductions = (emp.deductions || []).reduce((sum, d) => sum + Number(d.amount || 0), 0);
    return gross;
  };
  const calcNetWages = emp => {
    const rate = Number(emp.pay_rate) || 0;
    const units = Number(emp.pay_units) || 0;
    const frequencyFactor = (() => {
      switch (emp.pay_schedule) {
        case 'hourly': return (units * 52) / 12;
        case 'weekly': return 52 / 12;
        case 'biweekly': return 26 / 12;
        case 'semimonthly': return 2;
        case 'monthly': return 1;
        case 'yearly': return 1 / 12;
        default: return 0;
      }
    })();
    const gross = rate * frequencyFactor;
    const deductions = (emp.deductions || []).reduce((sum, d) => sum + Number(d.amount || 0), 0);
    return gross - deductions;
  };

  
    const caseDebtors = Array.isArray(caseData.debtors) ? caseData.debtors : [];
  const primaryDebtor = caseDebtors.find(d => d.is_primary && d.is_filing);
  const secondaryDebtor = caseDebtors.find(d => !d.is_primary && d.is_filing);
  const nonFilingSpouse = caseDebtors.find(d => !d.is_primary && !d.is_filing);

if ((selectedSchedules.includes('I') || selectedSchedules.includes('all'))){
    const iUrl = '/forms/i.pdf';
  const [iBytes] = await Promise.all([fetchPdf(iUrl)]);
  const pdfI = await PDFDocument.load(iBytes);
  const formI = pdfI.getForm();

  // Fill Schedule J fields
      if (primaryDebtor) {
        formI.getTextField('Debtor 1').setText(
          [primaryDebtor.first_name, primaryDebtor.second_name, primaryDebtor.last_name]
            .filter(Boolean).join(' ')
        );
      }
      if (secondaryDebtor) {
        formI.getTextField('Debtor 2').setText(
          [secondaryDebtor.first_name, secondaryDebtor.second_name, secondaryDebtor.last_name]
            .filter(Boolean).join(' ')
        );
      }
  formI.getTextField('district').setText('Eastern District of Tennessee');
  formI.getTextField('case_number').setText(caseData?.case_number || '');
    if (data.amended) formI.getCheckBox('amended').check()
  if (data.amended && caseData.case_number){
    if (caseData.chapter === '13'){
     formI.getCheckBox('includes_supplement').check();
     // Use today's date if amended, else execution_date
     const today = new Date();
     const todayISO = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
     formI.getTextField('supplement_date').setText(fmtDate(data.amended ? todayISO : caseData.execution_date));
  }}
  // Collect combined descriptions for both debtors
  const otherIncomeDescParts = [];
  const govAssistanceDescParts = [];
  const expenseContribDescParts = [];
  const deductionOtherDescParts = [];

  // Fill fields for each debtor (1 or 2)
  const debtors = []
  debtors.push(primaryDebtor)
  if (secondaryDebtor) debtors.push(secondaryDebtor)
    if (nonFilingSpouse) debtors.push(nonFilingSpouse)
  debtors.forEach((debtor, idx) => {
    const col = idx === 0 ? '1' : '2';
    const emp = debtor.employments[0] || {};
    // Employment info
    formI.getTextField(`Occupation Debtor ${col}`).setText(emp.occupation || '');
    const timeEmployedText = formatEmploymentLength(
      emp.length_of_employment,
      emp.start_date,
    );
    formI.getTextField(`Time employed Debtor ${col}`).setText(timeEmployedText);
    formI.getTextField(`Employers Name Debtor ${col}`).setText(emp.employer || '');
    formI.getTextField(`Employers Street1 Debtor ${col}`).setText(emp.employer_street || '');
    formI.getTextField(`Employers Street2 Debtor ${col}`).setText(emp.employer_street2 || '');
    formI.getTextField(`Employers City Debtor ${col}`).setText(emp.employer_city || '');
    formI.getTextField(`Employers State Debtor ${col}`).setText(emp.employer_state || '');
    formI.getTextField(`Employers Zip debtor ${col}`).setText(emp.employer_zip || '');
    // Employed / Unemployed checkboxes
    const hasJob = debtor.employments.length > 0;
    if (hasJob) formI.getCheckBox(`employed Debtor ${col}`).check();
    else formI.getCheckBox(`unemployed Debtor ${col}`).check();
    // Income lines
    const grossWages = emp.id ? calcGrossWages(emp).toFixed(2) : '0.00';
    formI.getTextField(`gross_wages Debtor ${col}`).setText(grossWages);
    formI.getTextField(`gross plus overtime Debtor ${col}`).setText(grossWages);
    formI.getTextField(`interest_dividends Debtor ${col}`).setText((Number(debtor.interest_dividend_income) || 0).toFixed(2));
    formI.getTextField(`child_support_alimony Debtor ${col}`).setText((Number(debtor.family_support_payments) || 0).toFixed(2));
    formI.getTextField(`unemployment Debtor ${col}`).setText((Number(debtor.unemployment_income) || 0).toFixed(2));
    formI.getTextField(`social_security Debtor ${col}`).setText((Number(debtor.social_security_income) || 0).toFixed(2));
    formI.getTextField(`pension Debtor ${col}`).setText((Number(debtor.pension_income) || 0).toFixed(2));

    // Deductions breakdown
    const dedTypes = {};
    (emp.deductions || []).forEach(d => {
      const amt = Number(d.amount) || 0;
      switch (d.deduction_type) {
        case 'taxes': dedTypes.tax = (dedTypes.tax||0) + amt; break;
        case 'mandatory_retirement': dedTypes.mandatory_retirement = (dedTypes.mandatory_retirement||0) + amt; break;
        case 'voluntary_retirement': dedTypes.voluntary_retirement = (dedTypes.voluntary_retirement||0) + amt; break;
        case 'retirement_loan': dedTypes.retirement_loan = (dedTypes.retirement_loan||0) + amt; break;
        case 'insurance': dedTypes.insurance = (dedTypes.insurance||0) + amt; break;
        case 'dso': dedTypes.child_support = (dedTypes.child_support||0) + amt; break;
        case 'union_dues': dedTypes.union_dues = (dedTypes.union_dues||0) + amt; break;
        case 'other':
          dedTypes.other = dedTypes.other || { desc: [], amt: 0 };
          dedTypes.other.desc.push(d.other_description);
          dedTypes.other.amt += amt;
          break;
        default: break;
      }
    });
    // Fill deduction fields
    formI.getTextField(`deduction_tax Debtor ${col}`).setText((dedTypes.tax||0).toFixed(2));
    formI.getTextField(`deduction_mandatory_retirement Debtor ${col}`).setText((dedTypes.mandatory_retirement||0).toFixed(2));
    formI.getTextField(`deduction_voluntary_retirement Debtor ${col}`).setText((dedTypes.voluntary_retirement||0).toFixed(2));
    formI.getTextField(`deduction_retirement_loan Debtor ${col}`).setText((dedTypes.retirement_loan||0).toFixed(2));
    formI.getTextField(`deduction_insurance Debtor ${col}`).setText((dedTypes.insurance||0).toFixed(2));
    formI.getTextField(`deduction_child_support Debtor ${col}`).setText((dedTypes.child_support||0).toFixed(2));
    formI.getTextField(`deduction_union_dues Debtor ${col}`).setText((dedTypes.union_dues||0).toFixed(2));
    if (dedTypes.other) {
      deductionOtherDescParts.push(`D${idx + 1}: ${dedTypes.other.desc.join(', ')}`);
      formI.getTextField(`deduction_other Debtor ${col}`).setText(dedTypes.other.amt.toFixed(2));
    } else {
      formI.getTextField(`deduction_other Debtor ${col}`).setText('0.00');
    }
  // Write combined "other deduction" descriptions for both debtors
  formI.getTextField('deduction_other_description').setText(
    deductionOtherDescParts.join('; ')
  );
    const deductionSum = Object.entries(dedTypes).reduce((sum, [k,v]) =>
      k === 'other' ? sum + v.amt : sum + (v||0), 0);
    formI.getTextField(`deduction_sum Debtor ${col}`).setText(deductionSum.toFixed(2));
    // Net pay = gross wages – deductions
    const netPay = parseFloat(grossWages) - deductionSum;
    formI.getTextField(`net_pay Debtor ${col}`).setText(netPay.toFixed(2));

    // Net Business Income for Schedule I
    const businessEntries = debtor.business_incomes || [];
    const totalNetBusiness = businessEntries
      .map(b => {
        const gross = Number(b.gross_revenue) || 0;
        const expensesSum = (b.expenses || []).reduce((sum, e) => sum + Number(e.amount || 0), 0);
        return gross - expensesSum;
      })
      .reduce((sum, n) => sum + n, 0);
    formI.getTextField(`net_business Debtor ${col}`).setText(totalNetBusiness.toFixed(2));

    // Other government assistance description & amount
    const govEntries = debtor.gov_assistances || [];
    // Build description segments for this debtor
    const govNames = govEntries.map(g => g.name).join(', ');
    // Only add government-assistance descriptor if there are entries
    if (govEntries.length > 0) {
      govAssistanceDescParts.push(`D${idx + 1}: ${govNames}`);
    }
    const govAmount = govEntries.reduce((s, g) => s + Number(g.amount||0), 0);
    formI.getTextField(`other_gov_assistance Debtor ${col}`).setText(govAmount.toFixed(2));

    // Include any “other income” items
    const otherEntries = (debtor.other_income_items || []).map(o => ({
      name: o.name,
      amount: Number(o.amount || 0),
    }));
    // Treat each additional employment beyond the first as “other income”
    debtor.employments.slice(1).forEach((empX, idxX) => {
      const netX = calcNetWages(empX);
      const label = (empX.employer && empX.employer.trim())
        ? empX.employer
        : `Employment ${idxX + 2}`;
      otherEntries.push({
        name: label,
        amount: netX,
      });
    });
    // Build description segments for this debtor
    const otherNames = otherEntries.map(o => o.name).join(', ');
    // Only add other-income descriptor if there are entries
    if (otherEntries.length > 0) {
      otherIncomeDescParts.push(`D${idx + 1}: ${otherNames}`);
    }
    const otherAmount = otherEntries.reduce((s, o) => s + Number(o.amount||0), 0);
    formI.getTextField(`other_income Debtor ${col}`).setText(otherAmount.toFixed(2));

    // Expense contributions description & amount
    const contribEntries = debtor.expense_contributions || [];
    const contribNames = contribEntries.map(c => c.name).join(', ');
    // Only add expense-contribution descriptor if there are entries
    if (contribEntries.length > 0) {
      expenseContribDescParts.push(`D${idx + 1}: ${contribNames}`);
    }
    const contribAmount = contribEntries.reduce((sum, c) => sum + Number(c.amount || 0), 0);
    expenseContribAmounts.push(contribAmount);

    // Total non-wage income = fixed lines + gov + other
    const fixedNonWage = (Number(debtor.interest_dividend_income)||0)
      + (Number(debtor.family_support_payments)||0)
      + (Number(debtor.social_security_income)||0)
      + (Number(debtor.unemployment_income)||0)
      + (Number(debtor.pension_income)||0);
    const totalNonWage = fixedNonWage + totalNetBusiness + govAmount + otherAmount;
    formI.getTextField(`total_non_wage_income Debtor ${col}`).setText(totalNonWage.toFixed(2));

    // Total income = gross_wages + total_non_wage
    const totalIncome = parseFloat(netPay) + totalNonWage;
    formI.getTextField(`total_income Debtor ${col}`).setText(totalIncome.toFixed(2));
  });

  // Write combined descriptions for both debtors only if present
  if (otherIncomeDescParts.length > 0) {
    formI.getTextField('other_income_description')
      .setText(otherIncomeDescParts.join('; '));
  }
  if (govAssistanceDescParts.length > 0) {
    formI.getTextField('other_gov_assistance_description')
      .setText(govAssistanceDescParts.join('; '));
  }
  const totalContribAmount = expenseContribAmounts.reduce((a, b) => a + b, 0);

    if (totalContribAmount > 0) {
      formI.getTextField('contribution_to_expenses_description_amount')
        .setText(totalContribAmount.toFixed(2));
            formI.getTextField('contribution_to_expenses_description')
      .setText(expenseContribDescParts.join('; '));
    }
  

  // Anticipated income change for debtors
  const expectedChangeParts = caseData.debtors.slice(0, 2).map((debtor, idx) => {
    const label = `D${idx + 1}`;
    if (debtor.anticipated_income_change) {
      return `${label}: ${debtor.anticipated_income_change_description || ''}`;
    } else {
      return `${label}: no anticipated change`;
    }
  });
  // Check the appropriate box based on any anticipated changes
  if (caseData.debtors.some(d => d.anticipated_income_change)) {
    formI.getCheckBox('expected_change_yes').check();
    formI.getTextField('expected_change_description')
    .setText(expectedChangeParts.join('; '));
  } else {
    formI.getCheckBox('expected_change_no').check();
  }
  

  // Combined total income
  const both = caseData.debtors.slice(0, 2).map((d, idx) =>
    parseFloat(formI.getTextField(`total_income Debtor ${idx===0?'1':'2'}`).getText()||0)
  );
  combined = both.reduce((a,b) => a+b, 0);
  
  formI.getTextField('total_income_both_debtors').setText(combined.toFixed(2));
  const combined_plus_contributions = (combined ?? 0) + (totalContribAmount ?? 0);
  formI.getTextField('total_income_plus_contributions').setText(combined_plus_contributions.toFixed(2));
  
  moveUncheckedCheckboxesToCorner(formI, pdfI.getPages()[0].getSize(), pdfI.context);

  // Supplemental pages for business incomes and additional employments
  const helveticaFont = await pdfI.embedFont(StandardFonts.Helvetica);
  const fontSize = 12;
  const lineHeight = fontSize + 2;

  const hasSupplement = caseData.debtors.some(d =>
    (d.business_incomes?.length || 0) > 0 || (d.employments?.length || 0) > 1
  );

  if (hasSupplement) {
    // Add a new page for supplemental details
    const suppPage = pdfI.addPage([612, 792]);
    let yPos = suppPage.getHeight() - 50;

    suppPage.drawText('Supplemental Income Details', {
      x: 50,
      y: yPos,
      size: 16,
      font: helveticaFont,
    });
    yPos -= 30;

    caseData.debtors.forEach((debtor, debIdx) => {
      const label = `Debtor ${debIdx + 1}: ${[debtor.first_name, debtor.second_name, debtor.last_name].filter(Boolean).join(' ')}`;
      suppPage.drawText(label, { x: 50, y: yPos, size: fontSize, font: helveticaFont });
      yPos -= lineHeight;

      // Business income details
      (debtor.business_incomes || []).forEach(biz => {
        suppPage.drawText(`Business: ${biz.name}`, { x: 70, y: yPos, size: fontSize, font: helveticaFont });
        yPos -= lineHeight;
        suppPage.drawText(`Gross Revenue: $${Number(biz.gross_revenue).toFixed(2)}`, { x: 90, y: yPos, size: fontSize, font: helveticaFont });
        yPos -= lineHeight;
        biz.expenses.forEach(exp => {
          suppPage.drawText(`Expense - ${exp.expense_name}: $${Number(exp.amount).toFixed(2)}`, {
            x: 90,
            y: yPos,
            size: fontSize,
            font: helveticaFont,
          });
          yPos -= lineHeight;
        });
        const netBiz = (Number(biz.gross_revenue) - (biz.expenses || []).reduce((s, e) => s + Number(e.amount || 0), 0)).toFixed(2);
        suppPage.drawText(`Net Business Income: $${netBiz}`, { x: 90, y: yPos, size: fontSize, font: helveticaFont });
        yPos -= lineHeight * 1.5;
      });

      // Additional employments
      debtor.employments.slice(1).forEach(emp => {
        suppPage.drawText(`Employment: ${emp.employer}`, { x: 70, y: yPos, size: fontSize, font: helveticaFont });
        yPos -= lineHeight;
        const addr = [emp.employer_street, emp.employer_street2, emp.employer_city, emp.employer_state, emp.employer_zip]
          .filter(Boolean).join(', ');
        suppPage.drawText(`Occuptation: ${emp.occupation || ''}`, { x: 90, y: yPos, size: fontSize, font: helveticaFont });
        yPos -= lineHeight;
        suppPage.drawText(`Address: ${addr}`, { x: 90, y: yPos, size: fontSize, font: helveticaFont });
        yPos -= lineHeight;
        suppPage.drawText(
          `Length: ${formatEmploymentLength(emp.length_of_employment, emp.start_date)}`,
          { x: 90, y: yPos, size: fontSize, font: helveticaFont },
        );
        yPos -= lineHeight;
        const grossEmp = calcGrossWages(emp).toFixed(2);
        suppPage.drawText(`Gross Wages: $${grossEmp}`, { x: 90, y: yPos, size: fontSize, font: helveticaFont });
        yPos -= lineHeight;
        // List each deduction separately
        (emp.deductions || []).forEach(d => {
          const desc = d.other_description ? ` (${d.other_description})` : '';
          suppPage.drawText(
            `Deduction - ${d.deduction_type}${desc}: $${Number(d.amount).toFixed(2)}`,
            { x: 90, y: yPos, size: fontSize, font: helveticaFont }
          );
          yPos -= lineHeight;
        });
        const netEmp = calcNetWages(emp).toFixed(2);
        suppPage.drawText(`Net Pay: $${netEmp}`, { x: 90, y: yPos, size: fontSize, font: helveticaFont });
        yPos -= lineHeight * 1.5;
      });
    });
  }
  
  formI.flatten();
  // Copy all pages (including supplement) directly from pdfI into mergedPdf
  const iPages = await mergedPdf.copyPages(pdfI, pdfI.getPageIndices());
  iPages.forEach(page => mergedPdf.addPage(page));
}

if ((selectedSchedules.includes('J') || selectedSchedules.includes('all'))){
  const jUrl = '/forms/j.pdf';
  const [jTemplateBytes] = await Promise.all([fetchPdf(jUrl)]);
  const pdfJ = await PDFDocument.load(jTemplateBytes);
  const formJ = pdfJ.getForm();


    if (primaryDebtor) {
    formJ.getTextField('Debtor 1').setText(
      [primaryDebtor.first_name, primaryDebtor.second_name, primaryDebtor.last_name]
        .filter(Boolean).join(' ')
    );
  }
  if (secondaryDebtor) {
    formJ.getTextField('Debtor 2').setText(
      [secondaryDebtor.first_name, secondaryDebtor.second_name, secondaryDebtor.last_name]
        .filter(Boolean).join(' ')
    );
  }
  formJ.getTextField('district').setText('Eastern District of Tennessee');
  formJ.getTextField('case_number').setText(caseData?.case_number || '');
  if (data.amended) formJ.getCheckBox('amended').check()
  if (data.amended && caseData.case_number){
     formJ.getCheckBox('includes_supplement').check();
     // Use today's date if amended, else execution_date
     const today = new Date();
     const todayISO = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
     formJ.getTextField('supplement_date').setText(fmtDate(data.amended ? todayISO : caseData.execution_date));
  }
  // Joint or individual case
  if (getFilingDebtors(caseData.debtors).length > 1) {
    formJ.getCheckBox('joint_case_yes').check();
  }
  else formJ.getCheckBox('joint_case_no').check();
  // Separated households
  if (expenseSheet.d2_separate_household) formJ.getCheckBox('separated_yes').check();
  else formJ.getCheckBox('separated_no').check();
  // Include non-dependent expenses?
  if (expenseSheet.account_for_nondependents) {
    formJ.getCheckBox('expenses_include_others_yes').check();
  } else {
    formJ.getCheckBox('expenses_include_others_no').check();
  }
  // J1 Dependents (primary household)
  const j1Dependents = dependents.filter(d => !d.j2);
  if (j1Dependents.length > 0) {
    formJ.getCheckBox('dependents_yes').check();
    const maxDisplay = 5;
    const totalJ1 = j1Dependents.length;
    j1Dependents.slice(0, maxDisplay).forEach((dep, i) => {
      const idx = i + 1;
      let rel = dep.relationship;
      let age = String(dep.age);
      let lives = dep.lives_with;
      if (i === maxDisplay - 1 && totalJ1 > maxDisplay) {
        const extras = j1Dependents.slice(maxDisplay - 1);
        rel = extras.map(e => e.relationship).join(', ');
        age = extras.map(e => e.age).join(', ');
        lives = extras.every(e => e.lives_with);
      }
      formJ.getTextField(`dependent_relation_${idx}`).setText(rel);
      formJ.getTextField(`dependent_age_${idx}`).setText(age);
      if (lives) formJ.getCheckBox(`dependent_lives_with_yes_${idx}`).check();
      else formJ.getCheckBox(`dependent_lives_with_no_${idx}`).check();
    });
  } else {
    formJ.getCheckBox('dependents_no').check();
  }
  // Separate base expense items and installment payments into each household
  const j1ExpenseItems = expenses.filter(e => !e.j2);
  const j2ExpenseItems = expenses.filter(e => e.j2);
  // Map installment payments into similar shape for inclusion in totals
  const j1Installments = installmentPayments
    .filter(p => !p.j2)
    .map(p => ({ amount: Number(p.amount || 0), description: p.description }));
  const j2Installments = installmentPayments
    .filter(p => p.j2)
    .map(p => ({ amount: Number(p.amount || 0), description: p.description }));
  // Combine expense items with installments for summing totals
  const j1Expenses = j1ExpenseItems.concat(j1Installments);
  const j2Expenses = j2ExpenseItems.concat(j2Installments);

  // Group and sum J1 expenses by category, join descriptions
  const j1Grouped = {};
  j1Expenses.forEach(e => {
    if (!e.category) return;
    if (!j1Grouped[e.category]) j1Grouped[e.category] = { amount: 0, descriptions: [] };
    j1Grouped[e.category].amount += Number(e.amount || 0);
    if (e.description) j1Grouped[e.category].descriptions.push(e.description);
  });
  Object.entries(j1Grouped).forEach(([cat, { amount, descriptions }]) => {
    const field = formJ.getTextField(cat);
    if (field) field.setText(amount.toFixed(2));
    let descField;
    try {
      descField = formJ.getTextField(`${cat}_description`);
    } catch {
      descField = null;
    }
    if (descriptions.length && descField) {
      descField.setText(descriptions.join(', '));
    }
  });


  // Totals
  formJ.getTextField('sum_of_expenses').setText(
    j1Expenses.reduce((s, e) => s + Number(e.amount || 0), 0).toFixed(2)
  );
  formJ.getTextField('sum_of_expenses_j2').setText(
    j2Expenses.reduce((s, e) => s + Number(e.amount || 0), 0).toFixed(2)
  );
  formJ.getTextField('total_expenses').setText(
    (j1Expenses.reduce((s, e) => s + Number(e.amount || 0), 0)
     + j2Expenses.reduce((s, e) => s + Number(e.amount || 0), 0)
    ).toFixed(2)
  );
  // Calculate total expenses for each household
  const j1Total = j1Expenses.reduce((sum, e) => sum + Number(e.amount || 0), 0);
  const j2Total = j2Expenses.reduce((sum, e) => sum + Number(e.amount || 0), 0);
  // Set total income and calculate monthly net income (income minus expenses)

  const totalContribAmount = expenseContribAmounts.reduce((a, b) => a + b, 0);
  const combined_plus_contributions = (combined ?? 0) + (totalContribAmount ?? 0);
  console.log(combined_plus_contributions)
  formJ.getTextField('total_income').setText(combined_plus_contributions.toFixed(2));
  const monthlyNet = combined_plus_contributions - (j1Total + j2Total);
  formJ.getTextField('monthly_net_income').setText(monthlyNet.toFixed(2));
  // Anticipated expense change
  const expChangeParts = caseData.debtors.slice(0, 2).map((d, i) => {
    const label = `D${i + 1}`;
    return d.anticipated_expense_change
      ? `${label}: ${d.anticipated_expense_change_description || ''}`
      : `${label}: no anticipated change`;
  });
  if (caseData.debtors.some(d => d.anticipated_expense_change)) {
    formJ.getCheckBox('anticipated_expense_change_yes').check();
    formJ.getTextField('anticipated_expense_change_description').setText(expChangeParts.join('; '));
  } else {
    formJ.getCheckBox('anticipated_expense_change_no').check();
  }
  
  // Installment payments (primary debtor only)
  const primaryInsts = installmentPayments.filter(p => !p.j2);
  // Clear previous entries
  ['installment_payment_1', 'installment_payment_2', 'installment_payment_3', 'installment_payment_4'].forEach(f => {
    if (formJ.getTextField(f)) formJ.getTextField(f).setText('0.00');
  });
  ['installment_payment_3_description', 'installment_payment_4_description'].forEach(f => {
    if (formJ.getTextField(f)) formJ.getTextField(f).setText('');
  });
  // First two installments
  primaryInsts.slice(0, 2).forEach((p, idx) => {
    const amt = Number(p.amount || 0).toFixed(2);
    formJ.getTextField(`installment_payment_${idx + 1}`).setText(amt);
  });
  // Third installment
  if (primaryInsts.length >= 3) {
    const p3 = primaryInsts[2];
    formJ.getTextField('installment_payment_3_description').setText(p3.description || '');
    formJ.getTextField('installment_payment_3').setText(Number(p3.amount || 0).toFixed(2));
  }
  // Fourth and any extras combined
  if (primaryInsts.length >= 4) {
    const extras = primaryInsts.slice(3);
    const total4 = extras.reduce((sum, p) => sum + Number(p.amount || 0), 0).toFixed(2);
    const desc4 = extras.map(p => p.description).filter(Boolean).join(', ');
    formJ.getTextField('installment_payment_4_description').setText(desc4);
    formJ.getTextField('installment_payment_4').setText(total4);
  }
  moveUncheckedCheckboxesToCorner(formJ, pdfJ.getPages()[0].getSize(), pdfJ.context);

  const j2Url = '/forms/j-2.pdf';
  const [j2TemplateBytes] = await Promise.all([fetchPdf(j2Url)]);
  const pdfJ2 = await PDFDocument.load(j2TemplateBytes);
  const formJ2 = pdfJ2.getForm();

  // Fill Schedule J-2 if secondary household is separate
  if (expenseSheet.d2_separate_household) {
    // Debtor names
    if (primaryDebtor) {
      formJ2.getTextField('Debtor 1').setText(
        [primaryDebtor.first_name, primaryDebtor.second_name, primaryDebtor.last_name]
          .filter(Boolean).join(' ')
      );
    }
    if (secondaryDebtor) {
      formJ2.getTextField('Debtor 2').setText(
        [secondaryDebtor.first_name, secondaryDebtor.second_name, secondaryDebtor.last_name]
          .filter(Boolean).join(' ')
      );
    }
    // Case details
    formJ2.getTextField('district').setText('Eastern District of Tennessee');
    formJ2.getTextField('case_number').setText(caseData.case_number || '');
    // Amended checkbox
      if (data.amended) formJ2.getCheckBox('amended').check()
      if (data.amended && caseData.case_number){
        formJ2.getCheckBox('includes_supplement').check();
        // Use today's date if amended, else execution_date
        const today = new Date();
        const todayISO = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        formJ2.getTextField('supplement_date').setText(fmtDate(data.amended ? todayISO : caseData.execution_date));
      }

    // Dependents for J-2 household
    const j2Deps = dependents.filter(d => d.j2);
    if (j2Deps.length > 0) {
      formJ2.getCheckBox('dependents_yes').check();
      const max = 5;
      j2Deps.slice(0, max).forEach((dep, i) => {
        const idx = i + 1;
        let rel = dep.relationship;
        let age = String(dep.age);
        let lives = dep.lives_with;
        if (i === max - 1 && j2Deps.length > max) {
          const extras = j2Deps.slice(max - 1);
          rel = extras.map(e => e.relationship).join(', ');
          age = extras.map(e => e.age).join(', ');
          lives = extras.every(e => e.lives_with);
        }
        formJ2.getTextField(`dependent_relation_${idx}`).setText(rel);
        formJ2.getTextField(`dependent_age_${idx}`).setText(age);
        if (lives) formJ2.getCheckBox(`dependent_lives_with_yes_${idx}`).check();
        else formJ2.getCheckBox(`dependent_lives_with_no_${idx}`).check();
      });
    } else {
      formJ2.getCheckBox('dependents_no').check();
    }
    // Include non-dependents?
    if (expenseSheet.account_for_nondependents_j2) formJ2.getCheckBox('expenses_include_others_yes').check();
    else formJ2.getCheckBox('expenses_include_others_no').check();
    // Group and fill J-2 expenses (including installments)
    const j2ExpItems = expenses.filter(e => e.j2)
    // Include J-2 installment payments in the expense total
    const j2InstallItems = installmentPayments.filter(p => p.j2).map(p => ({ amount: p.amount }));
    const j2AllExpenses = j2ExpItems.concat(j2InstallItems);
    const j2Grouped = {};
    const secondaryInstallments = installmentPayments.filter(p => !p.j2);
    // Clear previous entries
    ['installment_payment_1', 'installment_payment_2', 'installment_payment_3', 'installment_payment_4'].forEach(f => {
      if (formJ2.getTextField(f)) formJ2.getTextField(f).setText('0.00');
    });
    ['installment_payment_3_description', 'installment_payment_4_description'].forEach(f => {
      if (formJ2.getTextField(f)) formJ2.getTextField(f).setText('');
    });
    formJ2.getCheckBox('always_checked').check();
    // First two installments
    secondaryInstallments.slice(0, 2).forEach((p, idx) => {
      const amt = Number(p.amount || 0).toFixed(2);
      formJ2.getTextField(`installment_payment_${idx + 1}`).setText(amt);
    });
    // Third installment
    if (secondaryInstallments.length >= 3) {
      const p3 = secondaryInstallments[2];
      formJ2.getTextField('installment_payment_3_description').setText(p3.description || '');
      formJ2.getTextField('installment_payment_3').setText(Number(p3.amount || 0).toFixed(2));
    }
    // Fourth and any extras combined
    if (secondaryInstallments.length >= 4) {
      const extras = secondaryInstallments.slice(3);
      const total4 = extras.reduce((sum, p) => sum + Number(p.amount || 0), 0).toFixed(2);
      const desc4 = extras.map(p => p.description).filter(Boolean).join(', ');
      formJ2.getTextField('installment_payment_4_description').setText(desc4);
      formJ2.getTextField('installment_payment_4').setText(total4);
    }
    j2ExpItems.forEach(e => {
      const cat = e.category;
      if (!j2Grouped[cat]) j2Grouped[cat] = { amount: 0, desc: [] };
      j2Grouped[cat].amount += Number(e.amount || 0);
      if (e.description) j2Grouped[cat].desc.push(e.description);
    });
    Object.entries(j2Grouped).forEach(([cat, { amount, desc }]) => {
      if (formJ2.getTextField(cat)) formJ2.getTextField(cat).setText(amount.toFixed(2));
      const descField = `${cat}_description`;
      if (desc.length && formJ2.getTextField(descField)) {
        formJ2.getTextField(descField).setText(desc.join(', '));
      }
    });
    // Sum of expenses (including installment payments)
    const j2Total = j2AllExpenses.reduce((sum, e) => sum + Number(e.amount || 0), 0);
    formJ2.getTextField('sum_of_expenses').setText(j2Total.toFixed(2));
    // Anticipated expense change for D2
    if (secondaryDebtor && secondaryDebtor.anticipated_expense_change) {
      formJ2.getCheckBox('anticipated_expense_change_yes').check();
      formJ2.getTextField('anticipated_expense_change_description')
        .setText(secondaryDebtor.anticipated_expense_change_description || '');
    } else {
      formJ2.getCheckBox('anticipated_expense_change_no').check();
    }
    // Installment payments for J-2
    const j2Insts = installmentPayments.filter(p => p.j2);
    ['installment_payment_1','installment_payment_2','installment_payment_3','installment_payment_4']
      .forEach(f => formJ2.getTextField(f)?.setText('0.00'));
    ['installment_payment_3_description','installment_payment_4_description']
      .forEach(f => formJ2.getTextField(f)?.setText(''));
    j2Insts.slice(0,2).forEach((p, i) => {
      formJ2.getTextField(`installment_payment_${i+1}`)
        .setText(Number(p.amount||0).toFixed(2));
    });
    if (j2Insts[2]) {
      formJ2.getTextField('installment_payment_3_description')
        .setText(j2Insts[2].description || '');
      formJ2.getTextField('installment_payment_3')
        .setText(Number(j2Insts[2].amount||0).toFixed(2));
    }
    if (j2Insts.length > 3) {
      const extras = j2Insts.slice(3);
      formJ2.getTextField('installment_payment_4_description')
        .setText(extras.map(p => p.description).join(', '));
      formJ2.getTextField('installment_payment_4')
        .setText(extras.reduce((s, p) => s + Number(p.amount||0), 0).toFixed(2));
    }
    moveUncheckedCheckboxesToCorner(formJ2,pdfJ2.getPages()[0].getSize(), pdfJ2.context);
    // Do not download Schedule J-2 individually
  }

  // Merge Schedule J (and J-2 if present) into a single PDF
  
  // Save and load J pages
  formJ.flatten();
  const jBytes = await pdfJ.save();
  const jDoc = await PDFDocument.load(jBytes);
  const jPages = await mergedPdf.copyPages(jDoc, jDoc.getPageIndices());
  jPages.forEach(page => mergedPdf.addPage(page));
  // If J-2 was generated, merge it as well
  if (expenseSheet.d2_separate_household) {
    formJ2.flatten();
    const j2Bytes = await pdfJ2.save();
    const j2Doc = await PDFDocument.load(j2Bytes);
    const j2Pages = await mergedPdf.copyPages(j2Doc, j2Doc.getPageIndices());
    j2Pages.forEach(page => mergedPdf.addPage(page));
  }
}
  // Download merged PDF
  let decPdfDoc = null
  let amendedPdfDoc = null;
      if (data.amended){
        
          const decUrl = '/forms/dec.pdf'
          const decExistingPdfBytes = await fetch(decUrl).then(res => res.arrayBuffer());
          decPdfDoc = await PDFDocument.load(decExistingPdfBytes);
          const decForm = decPdfDoc.getForm();

          (data.caseInfo.debtors.filter(d => d.is_filing) || []).forEach((debtor, index) => {
              const amendPrefix = debtor.is_primary ? 'Debtor 1' : 'Debtor 2';
              decForm.getTextField(`${amendPrefix}`).setText(`${(debtor.first_name || '')} ${(debtor.second_name || '')} ${(debtor.last_name || '')} ${(debtor.suffix || '')}`);
          });

          (data.caseInfo.debtors.filter(d => d.is_filing) || []).forEach((debtor, index) => {
              const amendPrefix = debtor.is_primary ? 'Debtor1' : 'Debtor2';
              decForm.getTextField(`${amendPrefix}.signature`).setText(`s/ ${(debtor.first_name || '')} ${(debtor.second_name || '')} ${(debtor.last_name || '')} ${(debtor.suffix || '')}`);
              // Use today's date if amended, else execution_date
              const today = new Date();
              const todayISO = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
              decForm.getTextField(`${amendPrefix}.Executed on`).setText(fmtDate(data.amended ? todayISO : data.caseInfo.execution_date));
          });

          decForm.getCheckBox(`no_preparer`).check();
          decForm.getCheckBox(`amended`).check();
          decForm.getTextField('case_number').setText(data.caseInfo.case_number || '');
          decForm.getTextField('district').setText("Eastern District of Tennessee");
          const page1 = decPdfDoc.getPage(0);
          const width1 = page1.getWidth();
          const height1 = page1.getHeight();
          decForm.getFields().forEach(field => {
              if (field instanceof PDFCheckBox && !field.isChecked()) {
                field.acroField.getWidgets().forEach(widget => {
                  const dict = widget.acroAnnotation?.dict || widget.dict;
                  // move widget off-page by setting its Rect to a small area beyond the page bounds
                  dict.set(
                    PDFName.of('Rect'),
                    decPdfDoc.context.obj([
                      width1 + 5,   // lower-left x just outside page
                      height1 + 5,  // lower-left y just outside page
                      width1 + 15,  // upper-right x a bit further out
                      height1 + 15  // upper-right y a bit further out
                    ])
                  );
                });
              }
            });

          const helvetica = await decPdfDoc.embedFont(StandardFonts.Helvetica);
          decForm.updateFieldAppearances(helvetica);
          decForm.flatten();

          const amendedUrl = '/forms/notice-of-amendment.pdf'
          const amendedExistingPdfBytes = await fetch(amendedUrl).then(res => res.arrayBuffer());
          amendedPdfDoc = await PDFDocument.load(amendedExistingPdfBytes);
          const amendedForm = amendedPdfDoc.getForm();
  
          (data.caseInfo.debtors || []).forEach((debtor, index) => {
              const amendPrefix = debtor.is_primary ? 'debtor1' : 'debtor2';
              amendedForm.getTextField(`${amendPrefix}`).setText(`${(debtor.first_name || '')} ${(debtor.second_name || '')} ${(debtor.last_name || '')} ${(debtor.suffix || '')}`);
          });
          amendedForm.getTextField('case_number').setText(data.caseInfo.case_number || '');
          amendedForm.getTextField('chapter').setText(data.caseInfo.chapter || '');
          // Use today's date if amended, else execution_date
          const today = new Date();
          const todayISO = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
          amendedForm.getTextField('date').setText(fmtLongDate(data.amended ? todayISO : data.caseInfo.execution_date));
          // dynamically build amended files list excluding SOI, Matrix, and 'all'
          // determine which schedules to list when building amended labels
          const scheduleTypes = selectedSchedules.includes('all')
            ? ['I', 'J']
            : selectedSchedules.filter(type => !['SOI', 'Matrix', 'all'].includes(type));
          const scheduleLabels = scheduleTypes
            .map(type => `Schedule ${type}`)
            .join(', ');
          amendedForm.getTextField('amended_files').setText(`${scheduleLabels}`);
          amendedForm.getTextField('items_served').setText(`Amended ${scheduleLabels}, Declaration, and Notice of Amendment`);
          amendedForm.getTextField('reason_amended').setText(data.amendedReason);
          amendedForm.getTextField('creditors_served_description').setText(
            amendmentServiceDescription(data),
          );

          amendedForm.updateFieldAppearances(helvetica);
          amendedForm.flatten();
          
      }
          if (data.amended && amendedPdfDoc) {
        // then amended form
        const decPages = await mergedPdf.copyPages(decPdfDoc, decPdfDoc.getPageIndices());
        decPages.forEach(p => mergedPdf.addPage(p));
        const amendedPages = await mergedPdf.copyPages(amendedPdfDoc, amendedPdfDoc.getPageIndices());
        amendedPages.forEach(p => mergedPdf.addPage(p));
        // then creditor list
        await appendAmendmentServiceList(mergedPdf, data, generateCreditorsListPdf);
        const envelopes = await generateEnvelopesPdf(amendmentServiceRecipients(data));
      }

  let finalBlob
  if (selectedSchedules.includes('I') ||selectedSchedules.includes('J') || selectedSchedules.includes('all') ) {

  const mergedBytes = await mergedPdf.save();
   const blob = new Blob([mergedBytes], { type: 'application/pdf' });
    const formData2 = new FormData();
    formData2.append('file', blob, 'draft.pdf');
    const primaryName = [primaryDebtor.first_name, primaryDebtor.second_name, primaryDebtor.last_name]
      .filter(Boolean).join(' ');
          const response = await fetch(`${API_URL2}/optimize-pdf/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData2,
        credentials: 'include',
      });
      if (response.ok) {
        finalBlob = await response.blob();
      } else {
        console.error("Optimization failed");
        finalBlob = blob;  // fallback to original merged blob
      }

      // Optional: trigger download here
      //const link = document.createElement('a');
      //link.href = URL.createObjectURL(finalBlob);
      //link.download = response.ok ? `${primaryName}_ScheduleIJ.pdf` : `${primaryName}_ScheduleIJ-failed.pdf`;
      //link.click();

      
}
   const totalMonthly = inc => (
    ['gross_wages','alimony','household_expenses','biz_net_monthly',
     'real_net_monthly','interest_dividends','unemployment','pension']
      .reduce((sum, k) => sum + (parseFloat(inc[k]) || 0), 0) +
    (inc.other_incomes?.reduce((s,o) => s + (parseFloat(o.amount)||0), 0) || 0)
  );

  const inc1 = meansTestIncomes.find(inc => inc.debtor.is_primary) || {};
  const inc2 = meansTestIncomes.find(inc => !inc.debtor.is_primary) || {};
  const m1 = totalMonthly(inc1);
  const m2 = totalMonthly(inc2);
  const combinedMonthly = m1 + m2;
  const combinedYearly  = combinedMonthly * 12;

  // 2. Lookup median income by state and household size
  const STATE_NAMES = {
    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
    CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
    HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
    KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
    MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi',
    MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire',
    NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York', NC: 'North Carolina',
    ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania',
    RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee',
    TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington',
    WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming', DC: 'District of Columbia'
  };
  const stateCode = caseData.debtors?.[0]?.state || '';
  const householdSize = (caseData.debtors?.length || 0) + dependents?.length;
  const medianIncome = parseFloat(
    medianData[ STATE_NAMES[stateCode] ]?.[`${householdSize}`] || 0
  );

  if (selectedSchedules.includes('122') || selectedSchedules.includes('all')) {
  if (caseData.chapter === '7'){
  const monthlyIncome7Url = '/forms/122-7.pdf';
  const [monthlyIncome7Bytes] = await Promise.all([fetchPdf(monthlyIncome7Url)]);
  const pdfmonthlyIncome7 = await PDFDocument.load(monthlyIncome7Bytes);
  const form7 = pdfmonthlyIncome7.getForm();


  // ---------- Begin Schedule 122-7 (Means Test) ----------

  // 1. Compute monthly totals for each debtor including "other_incomes"
 
  // 3. No presumption vs long form
  const noPresumption = combinedYearly < medianIncome;
  const longForm      = !noPresumption;

  // 4. Fill Schedule 122-7 fields
  const caseDebtors = [];

  const primaryDebtor = caseData?.debtors?.find(d => d.is_primary && d.is_filing);
  const secondaryDebtor = caseData?.debtors?.find(d => !d.is_primary && d.is_filing);
  const nonFilingSpouse = caseData?.debtors?.find(d => !d.is_primary && !d.is_filing);
  caseDebtors.push(primaryDebtor)
    if (secondaryDebtor) caseDebtors.push(secondaryDebtor)
    if (nonFilingSpouse) caseDebtors.push(nonFilingSpouse)
  // Debtor names, case, district
  const _today7 = new Date();
  const _todayISO7 = `${_today7.getFullYear()}-${String(_today7.getMonth() + 1).padStart(2, '0')}-${String(_today7.getDate()).padStart(2, '0')}`;
  const _execISO7 = data.amended ? _todayISO7 : caseData.execution_date;
  const sigDate = fmtDate(_execISO7);
  if (primaryDebtor){ form7.getTextField('Debtor 1').setText(
    [primaryDebtor.first_name, primaryDebtor.second_name, primaryDebtor.last_name]
      .filter(Boolean).join(' ')
  )
  form7.getTextField('Debtor1 signature').setText(
    ['s/',primaryDebtor.first_name, primaryDebtor.second_name, primaryDebtor.last_name]
      .filter(Boolean).join(' ')
  )
  form7.getTextField('Debtor1 Date signed').setText(sigDate);
}
  if (secondaryDebtor) {form7.getTextField('Debtor 2').setText(
    [secondaryDebtor.first_name, secondaryDebtor.second_name, secondaryDebtor.last_name]
      .filter(Boolean).join(' '))
      form7.getTextField('Debtor2 signature').setText(
    ['s/',secondaryDebtor.first_name, secondaryDebtor.second_name, secondaryDebtor.last_name]
      .filter(Boolean).join(' ')
  )
  form7.getTextField('Debtor1 Date signed').setText(sigDate);
form7.getTextField('Debtor2 Date signed').setText(sigDate);
};
  form7.getTextField('district').setText('Eastern District of Tennessee');
  form7.getTextField('case_number').setText(caseData?.case_number || '');
  // Marital / filing status
  const filingDebtors = caseData.debtors.filter(d => d.is_filing);
  if (caseData.debtors.length === 2 && filingDebtors.length === 2) {
    form7.getCheckBox('married_filing_joint').check();
  } else if (caseData.debtors.length === 2 && filingDebtors.length === 1) {
    form7.getCheckBox('not_separated').check();
    form7.getCheckBox('married_filing_sole').check();
  } else if (caseData.debtors.length === 1 && caseData.debtors[0]?.marital_status === 'separated') {
    form7.getCheckBox('married_filing_sole').check();
    form7.getCheckBox('separated').check();
  } else {
    form7.getCheckBox('unmarried').check();
  }

  // Amended
  if (data.amended) form7.getCheckBox('amended').check();

  // Income lines for each debtor
  [['Debtor1', inc1], ['Debtor2', inc2]].forEach(([prefix, inc], idx) => {
    form7.getTextField(`${prefix} gross_wages`).setText((parseFloat(inc.gross_wages)||0).toFixed(2));
    form7.getTextField(`${prefix} alimony`).setText((parseFloat(inc.alimony)||0).toFixed(2));
    form7.getTextField(`${prefix} household_expenses`).setText((parseFloat(inc.household_expenses)||0).toFixed(2));
    form7.getTextField(`${prefix} biz_gross_receipts`).setText((parseFloat(inc.biz_gross_receipts)||0).toFixed(2));
    form7.getTextField(`${prefix} biz_expenses`).setText((parseFloat(inc.biz_expenses)||0).toFixed(2));
    form7.getTextField(`${prefix} biz_net_monthly`).setText((parseFloat(inc.biz_net_monthly)||0).toFixed(2));
    form7.getTextField(`${prefix} real_gross_receipts`).setText((parseFloat(inc.real_gross_receipts)||0).toFixed(2));
    form7.getTextField(`${prefix} real_expenses`).setText((parseFloat(inc.real_expenses)||0).toFixed(2));
    form7.getTextField(`${prefix} real_net_monthly`).setText((parseFloat(inc.real_net_monthly)||0).toFixed(2));
    form7.getTextField(`${prefix} interest_dividends`).setText((parseFloat(inc.interest_dividends)||0).toFixed(2));
    form7.getTextField(`${prefix} unemployment`).setText((parseFloat(inc.unemployment)||0).toFixed(2));
    form7.getTextField(`${prefix} social_security`).setText((parseFloat(inc.social_security)||0).toFixed(2));
    form7.getTextField(`${prefix} pension`).setText((parseFloat(inc.pension)||0).toFixed(2));
    // Other incomes: first and remainder, combined descriptions per debtor
    const otherEntries = inc.other_incomes || [];
    // First entry
    const firstEntry = otherEntries[0];
    const firstDesc = firstEntry ? firstEntry.description : '';
    const firstAmt = firstEntry ? parseFloat(firstEntry.amount) || 0 : 0;
    form7.getTextField(`${prefix} other_1_amount`).setText(firstAmt.toFixed(2));
    // Append description to shared first line only if non-empty
    if (firstDesc) {
      const field = form7.getTextField('other_1_description');
      const existing = field.getText() || '';
      const part = `D${idx+1}: ${firstDesc}`;
      field.setText(existing ? `${existing}; ${part}` : part);
    }
    // Remaining entries for second line
    const remainderEntries = otherEntries.slice(1);
    const remDesc = remainderEntries.map(o => o.description).filter(Boolean).join(', ');
    const remAmt = remainderEntries.reduce((sum, o) => sum + (parseFloat(o.amount) || 0), 0);
    form7.getTextField(`${prefix} other_2_amount`).setText(remAmt.toFixed(2));
    // Append description to shared second line only if non-empty
    if (remDesc) {
      const field = form7.getTextField('other_2_description');
      const existing = field.getText() || '';
      const part = `D${idx+1}: ${remDesc}`;
      field.setText(existing ? `${existing}; ${part}` : part);
    }
  });

  // Totals & presumption boxes
  form7.getTextField('Debtor1 total_monthly').setText(m1.toFixed(2));
  form7.getTextField('Debtor2 total_monthly').setText(m2.toFixed(2));
  form7.getTextField('total_monthly').setText(combinedMonthly.toFixed(2));
  form7.getTextField('yearly').setText(combinedYearly.toFixed(2));
  form7.getTextField('state').setText(stateCode);
  form7.getTextField('household_size').setText(String(householdSize));
  form7.getTextField('median_income').setText(medianIncome.toFixed(2));
  if (noPresumption){
  form7.getCheckBox('no_presumption').check();
  form7.getCheckBox('no_presumption2').check();
  }else{
  form7.getCheckBox('long_form').check();
  form7.getCheckBox('long_form2').check();
  }
  // Signatures and dates
  moveUncheckedCheckboxesToCorner(form7,pdfmonthlyIncome7.getPages()[0].getSize(), pdfmonthlyIncome7.context);
  // Flatten and download Schedule 122-7
  form7.flatten();
  const pdf1227Bytes = await pdfmonthlyIncome7.save();
  form122ShortBlob = new Blob([pdf1227Bytes], { type: 'application/pdf' });
  const doc1227 = await PDFDocument.load(pdf1227Bytes);
  const pages1227 = await mergedPdf122.copyPages(doc1227, doc1227.getPageIndices());
  pages1227.forEach(page => mergedPdf122.addPage(page));
} else {
  const monthlyIncome7Url = '/forms/122-13.pdf';
  const [monthlyIncome7Bytes] = await Promise.all([fetchPdf(monthlyIncome7Url)]);
  const pdfmonthlyIncome7 = await PDFDocument.load(monthlyIncome7Bytes);
  const form7 = pdfmonthlyIncome7.getForm();


  // ---------- Begin Schedule 122-7 (Means Test) ----------

  // 1. Compute monthly totals for each debtor including "other_incomes"
 
  // 3. No presumption vs long form
  const maritalAdj = maritalAdjustments.reduce((sum, adj) => sum + (parseFloat(adj.amount) || 0), 0);
  // Distribute marital adjustments across three lines
  const adjDescs = maritalAdjustments.map(adj => adj.description || '');
  const adjAmts = maritalAdjustments.map(adj => parseFloat(adj.amount) || 0);

  // Include amounts with each adjustment description
  const amt1 = adjAmts[0] || 0;
  const amt2 = adjAmts[1] || 0;
  const restAmts = adjAmts.slice(2);

  const desc1 = adjDescs[0]
    ? `${adjDescs[0]} ($${amt1.toFixed(2)})`
    : '';
  const desc2 = adjDescs[1]
    ? `${adjDescs[1]} ($${amt2.toFixed(2)})`
    : '';
  // Build third line by combining remaining descriptions with their amounts
  const restDescs = adjDescs.slice(2);
  const desc3 = restDescs
    .map((d, i) => `${d} ($${(restAmts[i] || 0).toFixed(2)})`)
    .join(', ');
  // Compute amounts for each line
  const amt3 = restAmts.reduce((sum, a) => sum + a, 0);

  // Fill marital adjustment fields
  form7.getTextField('marital_adjustment_description_1').setText(desc1);
  form7.getTextField('marital_adjustment_description_2').setText(desc2);
  form7.getTextField('marital_adjustment_description_3').setText(desc3);
  form7.getTextField('marital_adjustment_amount_1').setText(amt1.toFixed(2));
  form7.getTextField('marital_adjustment_amount_2').setText(amt2.toFixed(2));
  form7.getTextField('marital_adjustment_amount_3').setText(amt3.toFixed(2));

  const netIncomeLessMaritalAdj = combinedMonthly - maritalAdj;
  const noPresumption = (netIncomeLessMaritalAdj * 12) < medianIncome;
  const longForm = !noPresumption;
  const caseDebtors = Array.isArray(caseData.debtors) ? caseData.debtors : [];
  const primaryDebtor = caseDebtors.find(d => d.is_primary && d.is_filing);
  // 4. Fill Schedule 122-7 fields
  // Debtor names, case, district
  const _today13 = new Date();
  const _todayISO13 = `${_today13.getFullYear()}-${String(_today13.getMonth() + 1).padStart(2, '0')}-${String(_today13.getDate()).padStart(2, '0')}`;
  const _execISO13 = data.amended ? _todayISO13 : caseData.execution_date;
  const sigDate = fmtDate(_execISO13);
  if (primaryDebtor){ form7.getTextField('Debtor 1').setText(
    [primaryDebtor.first_name, primaryDebtor.second_name, primaryDebtor.last_name]
      .filter(Boolean).join(' ')
  )
  form7.getTextField('Debtor1 signature').setText(
    ['s/',primaryDebtor.first_name, primaryDebtor.second_name, primaryDebtor.last_name]
      .filter(Boolean).join(' ')
  )
  form7.getTextField('Debtor1 Date signed').setText(sigDate);
}
  if (secondaryDebtor) {form7.getTextField('Debtor 2').setText(
    [secondaryDebtor.first_name, secondaryDebtor.second_name, secondaryDebtor.last_name]
      .filter(Boolean).join(' '))
      form7.getTextField('Debtor2 signature').setText(
    ['s/',secondaryDebtor.first_name, secondaryDebtor.second_name, secondaryDebtor.last_name]
      .filter(Boolean).join(' ')
  )
  form7.getTextField('Debtor1 Date signed').setText(sigDate);
form7.getTextField('Debtor2 Date signed').setText(sigDate);
};
  form7.getTextField('district').setText('Eastern District of Tennessee');
  form7.getTextField('case_number').setText(caseData?.case_number || '');
  // Marital / filing status
  const filingDebtors = caseData.debtors.filter(d => d.is_filing);
  if (caseData.debtors.length === 2 && filingDebtors.length === 2) {
    form7.getCheckBox('married_filing_joint').check();
    form7.getCheckBox('married').check();
  } else if (caseData.debtors.length === 2 && filingDebtors.length === 1) {
    form7.getCheckBox('married_filing_sole').check();
    form7.getCheckBox('married').check();
  } else if (caseData.debtors.length === 1 && caseData.debtors[0]?.marital_status === 'separated') {
    form7.getCheckBox('married_filing_sole').check();
    form7.getCheckBox('married').check();
  } else {
    form7.getCheckBox('unmarried').check();
    form7.getCheckBox('unmarried2').check();
  }

  // Amended
  if (data.amended) form7.getCheckBox('amended').check();

  // Income lines for each debtor
  [['Debtor1', inc1], ['Debtor2', inc2]].forEach(([prefix, inc], idx) => {
    form7.getTextField(`${prefix} gross_wages`).setText((parseFloat(inc.gross_wages)||0).toFixed(2));
    form7.getTextField(`${prefix} alimony`).setText((parseFloat(inc.alimony)||0).toFixed(2));
    form7.getTextField(`${prefix} household_expenses`).setText((parseFloat(inc.household_expenses)||0).toFixed(2));
    form7.getTextField(`${prefix} biz_gross_receipts`).setText((parseFloat(inc.biz_gross_receipts)||0).toFixed(2));
    form7.getTextField(`${prefix} biz_expenses`).setText((parseFloat(inc.biz_expenses)||0).toFixed(2));
    form7.getTextField(`${prefix} biz_net_monthly`).setText((parseFloat(inc.biz_net_monthly)||0).toFixed(2));
    form7.getTextField(`${prefix} real_gross_receipts`).setText((parseFloat(inc.real_gross_receipts)||0).toFixed(2));
    form7.getTextField(`${prefix} real_expenses`).setText((parseFloat(inc.real_expenses)||0).toFixed(2));
    form7.getTextField(`${prefix} real_net_monthly`).setText((parseFloat(inc.real_net_monthly)||0).toFixed(2));
    form7.getTextField(`${prefix} interest_dividends`).setText((parseFloat(inc.interest_dividends)||0).toFixed(2));
    form7.getTextField(`${prefix} unemployment`).setText((parseFloat(inc.unemployment)||0).toFixed(2));
    form7.getTextField(`${prefix} social_security`).setText((parseFloat(inc.social_security)||0).toFixed(2));
    form7.getTextField(`${prefix} pension`).setText((parseFloat(inc.pension)||0).toFixed(2));
    // Other incomes: first and remainder, combined descriptions per debtor
    const otherEntries = inc.other_incomes || [];
    // First entry
    const firstEntry = otherEntries[0];
    const firstDesc = firstEntry ? firstEntry.description : '';
    const firstAmt = firstEntry ? parseFloat(firstEntry.amount) || 0 : 0;
    form7.getTextField(`${prefix} other_1_amount`).setText(firstAmt.toFixed(2));
    // Append description to shared first line
        if (firstDesc) {
      const field = form7.getTextField('other_1_description');
      const existing = field.getText() || '';
      const part = `D${idx+1}: ${firstDesc}`;
      field.setText(existing ? `${existing}; ${part}` : part);
    }
    // Remaining entries for second line
    const remainderEntries = otherEntries.slice(1);
    const remDesc = remainderEntries.map(o => o.description).filter(Boolean).join(', ');
    const remAmt = remainderEntries.reduce((sum, o) => sum + (parseFloat(o.amount) || 0), 0);
    form7.getTextField(`${prefix} other_2_amount`).setText(remAmt.toFixed(2));
    // Append description to shared second line only if non-empty
    if (remDesc) {
      const field = form7.getTextField('other_2_description');
      const existing = field.getText() || '';
      const part = `D${idx+1}: ${remDesc}`;
      field.setText(existing ? `${existing}; ${part}` : part);
    }
  });

  // Totals & presumption boxes
  form7.getTextField('Debtor1 total_monthly').setText(m1.toFixed(2));
  form7.getTextField('Debtor2 total_monthly').setText(m2.toFixed(2));
  form7.getTextField('total_monthly').setText(combinedMonthly.toFixed(2));
  form7.getTextField('marital_adjustment_amount_total').setText(maritalAdj.toFixed(2));
  form7.getTextField('total_monthly_less_marital_adjustment').setText(netIncomeLessMaritalAdj.toFixed(2));
  form7.getTextField('yearly').setText((netIncomeLessMaritalAdj*12).toFixed(2));
  form7.getTextField('state').setText(stateCode);
  form7.getTextField('household_size').setText(String(householdSize));
  form7.getTextField('median_income').setText(medianIncome.toFixed(2));
  if (noPresumption){
  form7.getCheckBox('no_presumption').check();
  form7.getCheckBox('no_presumption2').check();
  form7.getCheckBox('no_presumption3').check();
  form7.getCheckBox('no_presumption4').check();
  }else{
  form7.getCheckBox('long_form').check();
  form7.getCheckBox('long_form2').check();
  form7.getCheckBox('long_form3').check();
  form7.getCheckBox('long_form4').check();
  }
  // Signatures and dates
  moveUncheckedCheckboxesToCorner(form7,pdfmonthlyIncome7.getPages()[0].getSize(), pdfmonthlyIncome7.context);
  // Flatten and download Schedule 122-7
  form7.flatten();
  const pdf1227Bytes = await pdfmonthlyIncome7.save();
  form122ShortBlob = new Blob([pdf1227Bytes], { type: 'application/pdf' });
  const doc1227 = await PDFDocument.load(pdf1227Bytes);
  const pages1227 = await mergedPdf122.copyPages(doc1227, doc1227.getPageIndices());
  pages1227.forEach(page => mergedPdf122.addPage(page));

}
    const longFormPdf = await generateLongFormMeansTestPdf({
      caseData,
      dependents,
      expenses,
      creditors: data.allDebts || [],
      meansTestIncomes,
      maritalAdjustments,
      longFormData,
      amended: data.amended,
    });
    if (longFormPdf) {
      const longFormBytes = await longFormPdf.save();
      form122LongBlob = new Blob([longFormBytes], { type: 'application/pdf' });
      const longFormPages = await mergedPdf122.copyPages(longFormPdf, longFormPdf.getPageIndices());
      longFormPages.forEach(page => mergedPdf122.addPage(page));
    }
    let decPdfDoc = null
    let amendedPdfDoc = null;
        if (data.amended){
          
  
            const amendedUrl = '/forms/notice-of-amendment.pdf'
            const amendedExistingPdfBytes = await fetch(amendedUrl).then(res => res.arrayBuffer());
            amendedPdfDoc = await PDFDocument.load(amendedExistingPdfBytes);
            const amendedForm = amendedPdfDoc.getForm();
    
            (data.caseInfo.debtors || []).forEach((debtor, index) => {
                const amendPrefix = debtor.is_primary ? 'debtor1' : 'debtor2';
                amendedForm.getTextField(`${amendPrefix}`).setText(`${(debtor.first_name || '')} ${(debtor.second_name || '')} ${(debtor.last_name || '')} ${(debtor.suffix || '')}`);
            });
            amendedForm.getTextField('case_number').setText(data.caseInfo.case_number || '');
            amendedForm.getTextField('chapter').setText(data.caseInfo.chapter || '');
            {
              const __t = new Date();
              const __tISO = `${__t.getFullYear()}-${String(__t.getMonth() + 1).padStart(2, '0')}-${String(__t.getDate()).padStart(2, '0')}`;
              amendedForm.getTextField('date').setText(fmtLongDate(__tISO));
            }
            // dynamically build amended files list excluding SOI, Matrix, and 'all'
            // determine which schedules to list when building amended labels

            amendedForm.getTextField('amended_files').setText(`Amended Form 122`);
            amendedForm.getTextField('items_served').setText(`Amended Form 122 and Notice of Amendment`);
            amendedForm.getTextField('reason_amended').setText(data.amendedReason);
            amendedForm.getTextField('creditors_served_description').setText(
              amendmentServiceDescription(data),
            );
            const helvetica = await amendedPdfDoc.embedFont(StandardFonts.Helvetica);
            amendedForm.updateFieldAppearances(helvetica);
            amendedForm.flatten();
            
        }
            if (data.amended && amendedPdfDoc) {
          // then amended form
          const amendedPages = await mergedPdf122.copyPages(amendedPdfDoc, amendedPdfDoc.getPageIndices());
          amendedPages.forEach(p => mergedPdf122.addPage(p));
          // then creditor list
          await appendAmendmentServiceList(mergedPdf122, data, generateCreditorsListPdf);
          const envelopes = await generateEnvelopesPdf(amendmentServiceRecipients(data));
        }
    if (options.return122Parts) {
      return {
        shortForm: form122ShortBlob,
        longForm: form122LongBlob,
      };
    }

    const mergedBytes122 = await mergedPdf122.save();
     const blob = new Blob([mergedBytes122], { type: 'application/pdf' });
      const formData2 = new FormData();
      formData2.append('file', blob, 'draft.pdf');
      const caseDebtors = Array.isArray(caseData.debtors) ? caseData.debtors : [];
      const primaryDebtor = caseDebtors.find(d => d.is_primary && d.is_filing);
      const primaryName = [primaryDebtor.first_name, primaryDebtor.second_name, primaryDebtor.last_name]
        .filter(Boolean).join(' ');
      form122Blob = blob;
      try {
        const response = await fetch(`${API_URL2}/optimize-pdf/`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData2,
          credentials: 'include',
        });
        if (response.ok) form122Blob = await response.blob();
      } catch (error) {
        console.error('122 PDF optimization failed; using the generated PDF.', error);
      }
      if (options.download !== false) {
        const objectUrl = URL.createObjectURL(form122Blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = `${primaryName}_122.pdf`;
        link.click();
        URL.revokeObjectURL(objectUrl);
      }

}
  // ---------- End Schedule 122-7 ----------
return options.return122Only ? form122Blob : finalBlob;
}
